import { useMutation, useQuery } from '@apollo/client'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    GetUsersByEmailQuery,
    GrantClientRoleMutationVariables,
} from '__generated__/graphql'
import { useFormik } from 'formik'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    CanGrantClientRole,
    GrantClientRole,
} from 'recruiter/graphql/clients.gql'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'
import UsersByEmail from './UsersByEmail'

export interface AddClientModalProps {
    refetch: () => void
    onCancel: () => void
    open: boolean
    companyId: string
}

export interface GrantClientRoleFormValues {
    firstName: string
    lastName: string
    email: string
}

const AddClientModal: React.FC<AddClientModalProps> = ({
    onCancel,
    refetch,
    open,
    companyId,
}) => {
    const [grantClientRole] = useMutation(GrantClientRole)
    const [emailErrorMessage, setEmailErrorMessage] = useState<string>('')
    const toast = useToast()
    const [loading, setLoading] = useState(false)
    const [currentUser, setCurrentUser] = useState<
        GetUsersByEmailQuery['getUsersByEmail']['data'][number] | undefined
    >()

    const validationSchema: Yup.ObjectSchema<GrantClientRoleFormValues> =
        Yup.object({
            email: Yup.string()
                .required('Email is required')
                .email('Invalid email'),
            firstName: Yup.string()
                .required('First name is required')
                .trim()
                .strict(),
            lastName: Yup.string()
                .required('Last name is required')
                .trim()
                .strict(),
        })

    const formik = useFormik<GrantClientRoleFormValues>({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
        },
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: false,
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true)

            await handleGrantClientRole({
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                email: values.email.trim(),
            })
            setLoading(false)
            onClose()
        },
    })

    const { data: canGrantClientRoleData, refetch: alreadyClientQueryRefetch } =
        useQuery(CanGrantClientRole, {
            variables: { companyId, email: formik.values.email },
            skip: !formik.values.email || formik.values.email.trim() === '',
            onCompleted: (data) => {
                if (formik.values.email.trim() === '') {
                    setEmailErrorMessage('')
                    return
                }
                let message = ''

                if (data?.canGrantClientRole?.result === false) {
                    message = data?.canGrantClientRole?.message ?? ''
                } else if (formik.errors.email) {
                    message = formik.errors.email
                } else {
                    if (
                        Boolean(formik.errors.firstName) ||
                        Boolean(formik.errors.lastName)
                    ) {
                        message = data?.canGrantClientRole?.message ?? ''
                    }
                }
                setEmailErrorMessage(message)
            },
        })

    const formikRef = useRef(formik)

    useEffect(() => {
        if (currentUser) {
            // Update form fields with currentUser values
            formikRef.current.setFieldValue('email', currentUser.email ?? '')
            formikRef.current.setFieldValue(
                'firstName',
                currentUser.firstName ?? ''
            )
            formikRef.current.setFieldValue(
                'lastName',
                currentUser.lastName ?? ''
            )

            // Mark fields as touched if they have values
            if (currentUser.email && formikRef.current.values.email !== '') {
                formikRef.current.setFieldTouched('email', true, false)
            }
            if (
                currentUser.firstName &&
                formikRef.current.values.firstName !== ''
            ) {
                formikRef.current.setFieldTouched('firstName', true, false)
            }
            if (
                currentUser.lastName &&
                formikRef.current.values.lastName !== ''
            ) {
                formikRef.current.setFieldTouched('lastName', true, false)
            }

            alreadyClientQueryRefetch()
        } else {
            // Clear form fields if currentUser is undefined
            formikRef.current.setFieldValue('email', '')
            formikRef.current.setFieldValue('firstName', '')
            formikRef.current.setFieldValue('lastName', '')

            // Reset touched state
            formikRef.current.setFieldTouched('email', false, false)
            formikRef.current.setFieldTouched('firstName', false, false)
            formikRef.current.setFieldTouched('lastName', false, false)

            // Clear email error message
            setEmailErrorMessage('')
        }
    }, [currentUser, alreadyClientQueryRefetch])

    const onClose = () => {
        formik.resetForm()
        setCurrentUser(undefined)
        setEmailErrorMessage('')
        onCancel()
    }
    const handleGrantClientRole = (values: GrantClientRoleFormValues) => {
        const variables: GrantClientRoleMutationVariables = {
            companyId,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
        }

        grantClientRole({
            variables,
            onCompleted: (data) => {
                if (data?.grantClientRole?.success) {
                    toast({
                        title: 'Success!',
                        message:
                            data?.grantClientRole?.message ??
                            'Client added successfully',
                        severity: 'success',
                    })
                    refetch()
                    onClose()
                } else {
                    toast({
                        title: 'Error!',
                        message: `Failed to add client: ${data?.grantClientRole?.message}`,
                        severity: 'error',
                    })
                }
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: formatGraphQLError(
                        error,
                        `Failed to add candidate: ${error}`
                    ),
                })
                refetch()
                onClose()
            },
        })
    }
    const canEditName =
        currentUser &&
        (currentUser?.firstName === '' || currentUser?.lastName === '')

    const [emailError] = useMemo(() => {
        const touchedEmail = formik.touched.email
        const emailError = Boolean(formik.errors.email)

        return [touchedEmail && emailError]
    }, [formik.touched.email, formik.errors.email])

    const cantGrantClientRole = useMemo(() => {
        return canGrantClientRoleData?.canGrantClientRole?.result === false
    }, [canGrantClientRoleData])

    return (
        <Dialog onClose={onClose} open={open}>
            <Stack
                direction="column"
                spacing={2}
                alignItems={'flex-start'}
                justifyContent={'space-between'}
                display={'flex'}
                sx={{
                    padding: getPxAsRem(16, 24),
                    minWidth: getPxAsRem(600),
                    minHeight: getPxAsRem(325),
                }}
            >
                <Typography variant="h4">Add User</Typography>
                <Box display={'flex'} width="100%" minWidth={getPxAsRem(320)}>
                    <UsersByEmail
                        formik={formik}
                        serverColorOverride={
                            canGrantClientRoleData?.canGrantClientRole
                                .result === true && !formik.errors.email
                        }
                        error={emailError || cantGrantClientRole}
                        helperText={emailErrorMessage}
                        updateSelectedUser={setCurrentUser}
                        currentUser={currentUser}
                    />
                </Box>

                <Stack direction="row" spacing={2} width="100%">
                    <TextField
                        variant="standard"
                        fullWidth
                        disabled={!canEditName}
                        label="First Name"
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                        }
                        helperText={
                            formik.touched.firstName && formik.errors.firstName
                        }
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        variant="standard"
                        fullWidth
                        disabled={!canEditName}
                        label="Last Name"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                        }
                        helperText={
                            formik.touched.lastName && formik.errors.lastName
                        }
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>

                <Box width={'100%'} justifyContent="end" alignItems="end">
                    <DialogActions sx={{ padding: getPxAsRem(8) }}>
                        <Button variant="text" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            disabled={loading || cantGrantClientRole}
                            onClick={() => {
                                formik.handleSubmit()
                            }}
                        >
                            Add
                        </Button>
                    </DialogActions>
                </Box>
            </Stack>
        </Dialog>
    )
}

export default AddClientModal
